// GymTracker theme state.
//
// A theme is just { base: 'dark' | 'light', accent: '#rrggbb' }. Everything else
// (surfaces, hover/soft accent variants) is derived in CSS from those two values,
// so this stays tiny. Dark is the default when nothing is stored.
//
// Persistence is browser-only for now (localStorage), keyed per user so several
// people on the same browser keep their own look. A shared "last used" key is
// also written so the pre-login screen is already themed sensibly.

import { reactive, computed } from 'vue';

export const DEFAULT_ACCENT = '#3b82f6';

// Curated accent swatches for the "custom" picker (work on both dark & light).
export const ACCENT_PRESETS = [
  { name: 'Blå', value: '#3b82f6' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Lila', value: '#a855f7' },
  { name: 'Rosa', value: '#ec4899' },
  { name: 'Röd', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Bärnsten', value: '#f59e0b' },
  { name: 'Grön', value: '#22c55e' },
  { name: 'Smaragd', value: '#10b981' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
];

const LS_PREFIX = 'gymtracker.theme';
const LS_DEFAULT_KEY = `${LS_PREFIX}:_default`;

function keyFor(username) {
  return username ? `${LS_PREFIX}:${username}` : LS_DEFAULT_KEY;
}

function isHexColor(v) {
  return typeof v === 'string' && /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(v.trim());
}

// Relative luminance (sRGB) → pick readable text colour on top of the accent.
export function contrastColor(hex) {
  if (!isHexColor(hex)) return '#ffffff';
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const toLin = c => {
    const s = parseInt(c, 16) / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * toLin(h.slice(0, 2)) + 0.7152 * toLin(h.slice(2, 4)) + 0.0722 * toLin(h.slice(4, 6));
  return L > 0.5 ? '#14181f' : '#ffffff';
}

export const theme = reactive({
  base: 'dark',
  accent: DEFAULT_ACCENT,
});

// "dark" | "light" | "custom" — the high-level choice the UI shows.
export const themeMode = computed(() =>
  theme.accent.toLowerCase() === DEFAULT_ACCENT ? theme.base : 'custom'
);

// Text colour to draw on top of accent-filled surfaces.
export const onAccent = computed(() => contrastColor(theme.accent));

// Inline style object bound to .gym-root so the accent flows into all color-mix
// derivations, plus the data attribute for the base palette.
export const themeStyle = computed(() => ({
  '--gym-accent': theme.accent,
  '--gym-on-accent': onAccent.value,
}));

let currentUser = null;

function readStored(username) {
  for (const k of [keyFor(username), LS_DEFAULT_KEY]) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const base = parsed.base === 'light' ? 'light' : 'dark';
      const accent = isHexColor(parsed.accent) ? parsed.accent : DEFAULT_ACCENT;
      return { base, accent };
    } catch { /* ignore corrupt entry, try next */ }
  }
  return null;
}

function persist() {
  const payload = JSON.stringify({ base: theme.base, accent: theme.accent });
  try {
    localStorage.setItem(keyFor(currentUser), payload);
    localStorage.setItem(LS_DEFAULT_KEY, payload);
  } catch { /* storage may be unavailable (private mode) — non-fatal */ }
}

// Load the saved theme for a user (or the shared default). Call on app start and
// again right after login. Falls back to the dark default when nothing is stored.
export function loadTheme(username = null) {
  currentUser = username || null;
  const stored = readStored(currentUser);
  theme.base = stored?.base ?? 'dark';
  theme.accent = stored?.accent ?? DEFAULT_ACCENT;
}

export function setBase(base) {
  theme.base = base === 'light' ? 'light' : 'dark';
  persist();
}

export function setAccent(accent) {
  if (isHexColor(accent)) {
    theme.accent = accent.length === 4
      ? '#' + accent.slice(1).split('').map(c => c + c).join('')
      : accent.toLowerCase();
    persist();
  }
}

// Quick presets the segmented control uses.
export function applyPreset(mode) {
  if (mode === 'dark') { theme.base = 'dark'; theme.accent = DEFAULT_ACCENT; }
  else if (mode === 'light') { theme.base = 'light'; theme.accent = DEFAULT_ACCENT; }
  // 'custom' keeps current base/accent; the picker drives it from there.
  persist();
}
